/**
 * App-chrome locale resolution for static UI strings.
 *
 * Spanish renders whenever the browser reports an es/es-* language — the same
 * condition the server-driven question content has always used — so the shell
 * and the question forms agree. Anything else falls back to English, never raw
 * keys or blanks.
 *
 * NOTE: question content (`resolveLocalizedText` in questionUtils.ts) resolves
 * per-message from server localization arrays; `catalogLanguage()` in
 * workflowLabels.ts delegates to browserLanguageIsSpanish below. Unifying all
 * resolution on this module remains follow-up cleanup.
 */

export type AppLocale = "en" | "es";

const SPANISH_LANGUAGE_PATTERN = /^es(-|$)/i;

/**
 * The browser's language tag, safe to call where `navigator` may not exist
 * (node-based tooling importing this module graph). Falls back to English —
 * the same fail-closed default as everything else in this file.
 */
export const browserLanguage = (): string =>
  typeof navigator === "undefined" ? "en" : navigator.language;

/**
 * Whether a BCP-47 language tag is Spanish. The single language-matching
 * primitive — catalogLanguage (workflowLabels.ts) delegates here so the
 * chrome and the bilingual catalog can never drift in what counts as
 * "Spanish".
 */
export const browserLanguageIsSpanish = (
  language: string = browserLanguage(),
): boolean => SPANISH_LANGUAGE_PATTERN.test(language);

/**
 * Resolve the locale for static UI strings. The parameter exists so tests can
 * exercise every path without mutating globals or resetting module caches.
 */
export const resolveAppLocale = (
  language: string = browserLanguage(),
): AppLocale => (browserLanguageIsSpanish(language) ? "es" : "en");
